import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

export default function NightAndGroupSection() {
  const { ref: titleRef, isVisible: titleVis } = useScrollAnimation({ threshold: 0.15 });
  const { ref: nightRef, isVisible: nightVis } = useScrollAnimation({ threshold: 0.1 });
  const { ref: groupRef, isVisible: groupVis } = useScrollAnimation({ threshold: 0.1 });

  const handleBook = () => {
    const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (btn) btn.click();
  };

  const nightPerks = [
    { icon: 'ri-moon-line', text: 'Sessions available until 9pm — Mon through Sat' },
    { icon: 'ri-calendar-check-line', text: 'Perfect for students, working adults & busy families' },
    { icon: 'ri-sun-foggy-line', text: 'Same world-class instruction — day or night' },
    { icon: 'ri-door-open-line', text: 'Walk-ins welcome for evening slots too' },
  ];

  const groupPerks = [
    { icon: 'ri-group-line', text: 'Bring a friend and learn together side-by-side' },
    { icon: 'ri-money-dollar-circle-line', text: 'Group discounts available — ask our team' },
    { icon: 'ri-emotion-happy-line', text: 'More fun, more energy, more motivation' },
    { icon: 'ri-trophy-line', text: 'Friendly competition that pushes everyone to grow' },
  ];

  return (
    <section className="py-28 bg-white relative overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#EEF2FF]/60 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FFFBEB]/80 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-8">

        {/* Section header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className={`scroll-hidden ${titleVis ? 'scroll-visible' : ''} inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs font-bold uppercase tracking-widest border border-brand-yellow/30 mb-5`}>
            More Ways to Train
          </span>
          <h2 className={`scroll-hidden delay-100 ${titleVis ? 'scroll-visible' : ''} font-heading text-5xl lg:text-6xl font-black text-brand-blue-dark mb-4 leading-tight`}>
            Night Classes &amp;{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#7C3AED]">Group Sessions</span>
          </h2>
          <p className={`scroll-hidden delay-200 ${titleVis ? 'scroll-visible' : ''} font-body text-gray-500 text-base max-w-2xl mx-auto leading-relaxed`}>
            Life gets busy — that's why we stay open late. And everything is better with a friend. Bring yours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* ── Night Classes Card ── */}
          <div
            ref={nightRef}
            className={`scroll-hidden slide-left ${nightVis ? 'scroll-visible' : ''} rounded-3xl overflow-hidden`}
          >
            {/* Dark header */}
            <div className="relative bg-gradient-to-br from-[#020818] via-[#0F172A] to-[#1a1040] px-10 py-10 overflow-hidden">

              {/* Nebula / aurora glow blobs */}
              <div className="nebula-1 absolute -top-10 right-0 w-56 h-56 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 70%)' }} />
              <div className="nebula-2 absolute bottom-0 left-0 w-44 h-44 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(30,58,138,0.4) 0%, transparent 70%)' }} />
              <div className="nebula-1 absolute top-1/2 left-1/3 w-32 h-32 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%)', animationDelay: '5s' }} />

              {/* Shooting stars */}
              <div className="shooting-star-1 absolute rounded-full pointer-events-none"
                style={{ top: '18%', right: '12%', height: '1.5px', transformOrigin: 'right center',
                  background: 'linear-gradient(to left, rgba(255,255,255,0.9), transparent)', rotate: '-35deg' }} />
              <div className="shooting-star-2 absolute rounded-full pointer-events-none"
                style={{ top: '35%', right: '28%', height: '1px', transformOrigin: 'right center',
                  background: 'linear-gradient(to left, rgba(245,158,11,0.85), transparent)', rotate: '-30deg' }} />

              {/* Star field — white stars */}
              {([
                { top:'6%',  left:'5%',  s:2,   d:'0s',    c:'star-twinkle-slow' },
                { top:'14%', left:'18%', s:1.5, d:'0.6s',  c:'star-twinkle-fast' },
                { top:'8%',  left:'33%', s:1,   d:'1.4s',  c:'star-twinkle' },
                { top:'22%', left:'48%', s:1.5, d:'0.3s',  c:'star-twinkle-fast' },
                { top:'5%',  left:'62%', s:1,   d:'2.1s',  c:'star-twinkle-slow' },
                { top:'18%', left:'74%', s:2,   d:'0.9s',  c:'star-twinkle' },
                { top:'10%', left:'88%', s:1,   d:'1.7s',  c:'star-twinkle-fast' },
                { top:'32%', left:'8%',  s:1,   d:'0.5s',  c:'star-twinkle' },
                { top:'40%', left:'25%', s:1.5, d:'1.1s',  c:'star-twinkle-slow' },
                { top:'38%', left:'55%', s:1,   d:'0.2s',  c:'star-twinkle-fast' },
                { top:'28%', left:'82%', s:1.5, d:'1.8s',  c:'star-twinkle' },
                { top:'50%', left:'14%', s:1,   d:'2.4s',  c:'star-twinkle-slow' },
                { top:'55%', left:'40%', s:1.5, d:'0.8s',  c:'star-twinkle' },
                { top:'48%', left:'68%', s:1,   d:'1.5s',  c:'star-twinkle-fast' },
                { top:'60%', left:'90%', s:2,   d:'0.4s',  c:'star-twinkle-slow' },
                { top:'70%', left:'22%', s:1,   d:'1.9s',  c:'star-twinkle' },
                { top:'72%', left:'58%', s:1.5, d:'0.7s',  c:'star-twinkle-fast' },
                { top:'80%', left:'78%', s:1,   d:'2.6s',  c:'star-twinkle-slow' },
              ] as { top:string; left:string; s:number; d:string; c:string }[]).map((star, i) => (
                <div key={i} className={`absolute rounded-full bg-white pointer-events-none ${star.c}`}
                  style={{ top: star.top, left: star.left, width: star.s, height: star.s, animationDelay: star.d }} />
              ))}

              {/* Gold-tinted accent stars */}
              {([
                { top:'20%', left:'42%', s:2,   d:'1.3s' },
                { top:'45%', left:'78%', s:1.5, d:'0.1s' },
                { top:'65%', left:'35%', s:2,   d:'2.0s' },
                { top:'75%', left:'55%', s:1.5, d:'0.8s' },
              ] as { top:string; left:string; s:number; d:string }[]).map((star, i) => (
                <div key={i} className={`absolute rounded-full pointer-events-none star-twinkle`}
                  style={{ top: star.top, left: star.left, width: star.s, height: star.s,
                    backgroundColor: 'rgba(245,158,11,0.9)', animationDelay: star.d }} />
              ))}

              <div className="relative z-10 flex items-start gap-5">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow flex-shrink-0">
                  <i className="ri-moon-fill text-brand-blue-dark text-2xl" />
                </div>
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow/40 text-brand-yellow font-body text-xs font-semibold uppercase tracking-widest mb-3">
                    Open Until 9pm
                  </span>
                  <h3 className="font-heading text-white text-3xl font-black leading-tight">
                    Night Classes<br />
                    <span className="text-brand-yellow">Now Available</span>
                  </h3>
                  <p className="font-body text-white/60 text-sm mt-2 leading-relaxed">
                    Mon – Sat · 9am – 9pm
                  </p>
                </div>
              </div>
            </div>

            {/* White body */}
            <div className="bg-white border-x border-b border-gray-100 rounded-b-3xl px-10 py-8">
              <p className="font-body text-gray-600 text-base leading-relaxed mb-7">
                We know the grind doesn't stop at 5. That's why we run sessions all the way to 9pm — so students, working adults, and busy families can train without sacrificing their schedule.
              </p>
              <ul className="flex flex-col gap-4 mb-8">
                {nightPerks.map((perk) => (
                  <li key={perk.text} className="flex items-center gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#0F172A]/8 text-brand-blue-dark flex-shrink-0">
                      <i className={`${perk.icon} text-base`} />
                    </div>
                    <span className="font-body text-gray-600 text-sm leading-snug">{perk.text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleBook}
                className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#0F172A] to-[#1E3A8A] text-white font-body font-bold text-sm cursor-pointer hover:opacity-85 transition-all"
              >
                <i className="ri-calendar-check-line text-base" /> Book an Evening Session
              </button>
            </div>
          </div>

          {/* ── Group Sessions Card ── */}
          <div
            ref={groupRef}
            className={`scroll-hidden slide-right ${groupVis ? 'scroll-visible' : ''} rounded-3xl overflow-hidden`}
          >
            {/* Warm header */}
            <div className="relative bg-gradient-to-br from-[#D97706] via-[#B45309] to-[#92400E] px-10 py-10 overflow-hidden">
              {/* Confetti-like decoration */}
              <div className="absolute top-5 right-10 w-6 h-6 rounded-full bg-white/10" />
              <div className="absolute top-12 right-28 w-3 h-3 rounded-full bg-white/15" />
              <div className="absolute bottom-6 right-8 w-4 h-4 rounded-full bg-white/10" />
              <div className="absolute bottom-10 right-20 w-2 h-2 rounded-full bg-white/20" />

              <div className="relative z-10 flex items-start gap-5">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/20 flex-shrink-0">
                  <i className="ri-group-fill text-white text-2xl" />
                </div>
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-white/20 border border-white/30 text-white font-body text-xs font-semibold uppercase tracking-widest mb-3">
                    Bring a Friend
                  </span>
                  <h3 className="font-heading text-white text-3xl font-black leading-tight">
                    Group Sessions<br />
                    <span className="text-brand-blue-dark bg-brand-yellow px-2 rounded-lg inline-block mt-1">More Fun Together</span>
                  </h3>
                </div>
              </div>
            </div>

            {/* White body */}
            <div className="bg-white border-x border-b border-gray-100 rounded-b-3xl px-10 py-8">
              <p className="font-body text-gray-600 text-base leading-relaxed mb-7">
                Bring a friend, a sibling, or your whole crew. Group sessions create energy that solo practice can't match — and you push each other to levels you wouldn't reach alone.
              </p>
              <ul className="flex flex-col gap-4 mb-8">
                {groupPerks.map((perk) => (
                  <li key={perk.text} className="flex items-center gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow-dark flex-shrink-0">
                      <i className={`${perk.icon} text-base`} />
                    </div>
                    <span className="font-body text-gray-600 text-sm leading-snug">{perk.text}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#D97706] to-[#B45309] text-white font-body font-bold text-sm cursor-pointer hover:opacity-85 transition-all"
                >
                  <i className="ri-group-line text-base" /> Ask About Group Rates
                </Link>
                <Link
                  to="/enroll"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-gray-200 text-gray-600 font-body font-semibold text-sm cursor-pointer hover:border-brand-yellow hover:text-brand-yellow-dark transition-colors"
                >
                  Enroll Together
                </Link>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom nudge banner */}
        <div className={`scroll-hidden delay-300 ${groupVis ? 'scroll-visible' : ''} mt-8 rounded-2xl bg-gradient-to-r from-[#F0F4FF] to-[#F5F3FF] border border-brand-purple/10 px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-5`}>
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-brand-yellow flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-lg" />
            </div>
            <p className="font-body text-brand-blue-dark text-sm leading-snug">
              <strong className="font-bold">Not sure where to start?</strong> Our AI assistant can help you find the perfect time slot — day, evening, solo, or group.
            </p>
          </div>
          <button
            onClick={handleBook}
            className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-sm cursor-pointer hover:opacity-90 transition-all flex-shrink-0"
          >
            <i className="ri-calendar-2-line text-base" /> Find Your Time Slot
          </button>
        </div>

      </div>
    </section>
  );
}
